import { useNavigate, useParams } from 'react-router-dom';
import { StepNavigationButtons } from './StepNavigationButtons';

import { useEffect } from 'react';
import { trackStartVerificationEvent } from '~/analytics/eventTracker';
import { ProductType } from '~/models/product/ProductType';
import { Route_InternetLandingPage } from '../../../internet/InternetLandingPage';
import { Route_InternetOfferSubPage } from '../../../internet/InternetOfferSubPage';
import { campaignRoutes, StepType, useFlowSteps } from '../../campaign.routes';
import { useBasket } from '../../useBasket';
import { startCustomerVerification } from '../../verification/vipps/api';

export const StepNavigation: React.FC<{ isSummaryOpen: boolean; currentStep: number }> = ({ isSummaryOpen, currentStep }) => {
  const navigate = useNavigate();
  const hasInternetProduct = useBasket((s) => s.hasInternetProduct)();
  const setStepError = useBasket((s) => s.setStepError);
  const setPostVerificationRedirect = useBasket((s) => s.setPostVerificationRedirect);
  const flowsteps = useFlowSteps();
  const { campaignUrl } = useParams();

  // ensure all previous step conditions are met
  useEffect(() => {
    const currentStepData = flowsteps[currentStep];
    // 1. ensure previous steps are valid, otherwise redirect there
    for (const step of flowsteps.slice(0, currentStep)) {
      if (!isStepValid(step.type, campaignUrl, currentStepData.type)) {
        console.warn(`Previous step for ${currentStepData.title} is invalid (${step.title}). Redirecting to ${step.stepPath}`);
        navigate(step.stepPath, { replace: true });
        break;
      }
    }
  }, [currentStep, navigate, flowsteps, campaignUrl]);

  const onNext = () => {
    const stepType = flowsteps[currentStep].type;
    if (!isStepValid(stepType, campaignUrl, undefined)) {
      setStepError(stepErrorMessages[stepType]);
      return;
    }
    setStepError(undefined);
    // On contact info step we first need to perform verification
    if (stepType === 'contactInfo') {
      const basketState = useBasket.getState();
      // Missing verification data, so we redirect to provider
      if (!basketState.verificationResponse) {
        setPostVerificationRedirect(location.pathname);
        // Last event before we hand the user to the provider, see trackStartVerificationEvent
        trackStartVerificationEvent(basketState.selectedVerificationProvider);
        startCustomerVerification(basketState.selectedVerificationProvider).then((res) => {
          window.location.href = res;
        });
        return;
      }
      // Validate contact form
      const contactForm = document.querySelector('form[name="contactInfo"]') as HTMLFormElement | null;
      if (!contactForm) return;
      // If valid, call submit to store data in basket store
      if (contactForm.reportValidity() !== true) {
        contactForm.setAttribute('data-submitted', 'true');
        return;
      }
      // Validate delayed activation
      // TODO: add to e2e testing
      if (basketState.useDelayedActivation && basketState.delayedActivationDate == null) {
        const datePicker = document.querySelector('input[name="delayedActivationDate"]');
        if (datePicker instanceof HTMLInputElement) {
          datePicker.setCustomValidity('Du må velge en dato for utsatt aktivering');
          datePicker.reportValidity();
        }
        return;
      }

      // Submit form so that we store contact info to basket
      contactForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
    navigate(flowsteps[currentStep + 1].stepPath);
  };

  return (
    <StepNavigationButtons
      isSummaryOpen={isSummaryOpen}
      isLastStep={currentStep === flowsteps.length - 1}
      onNext={onNext}
      onPrev={() => {
        const stepPath = flowsteps[currentStep - 1]?.stepPath ?? campaignRoutes.index;
        if (stepPath === campaignRoutes.index && hasInternetProduct) {
          navigate(`/${Route_InternetLandingPage}/${Route_InternetOfferSubPage}`);
        } else {
          navigate(stepPath);
        }
      }}
    />
  );
};

const isStepValid = (type: StepType, campaignUrl: string | undefined, currentStepType?: StepType): boolean => {
  switch (type) {
    case 'premium': {
      // On fleksipakken the user must actively pick an included content option
      if (campaignUrl === 'fleksipakken') {
        return useBasket.getState().flexipakkenChoice != null;
      }
      return true;
    }
    case 'summary': {
      return true;
    }
    case 'equipment': {
      // must select a receiver
      return useBasket.getState().selectedProducts.some((sp) => sp.productType === ProductType.Receiver);
    }
    case 'internet':
      return Boolean(useBasket.getState().selectedISP);
    case 'contactInfo': {
      // 1. check has verification provider and response
      if (useBasket.getState().selectedVerificationProvider === '') {
        return false;
      }
      // special case for summary to ensure we have valid customer data
      if (currentStepType === 'summary') {
        const state = useBasket.getState();
        return state.verificationResponse != null && state.contactInfo != null;
      }
      // 2. contact form validity checked in onNext
      return true;
    }
  }
};

const stepErrorMessages: Record<StepType, string> = {
  premium: 'Du må velge inkludert innhold for å gå videre.',
  equipment: 'Du må velge minst én TV-boks.',
  internet: 'Du må velge et av alternativene.',
  contactInfo: 'Du må velge en verifiseringsmetode.',
  summary: '',
};
